import {
  toClientViewModel,
  toAddTemplateRequest,
  toUpdateTemplateRequest,
  toCreateClientRequest
} from '../../../core/client/client.core.js';
import { republishSummary } from '../../../core/client/republish.core.js';

export class ClientDetailEventAdapter {
  #apiAdapter;
  #clientDetailComponent;
  #notificationComponent;
  #reloadPage;
  /** Counts billing loads, so the answer to an earlier load never replaces a later one. */
  #billingLoads = 0;
  /** Counts history loads per item id, so an older answer never replaces a newer one. */
  #historyLoads = new Map();

  /**
   * @param {() => void} [reloadPage] reloads the app so index.js shows the login screen
   */
  constructor(apiAdapter, clientDetailComponent, notificationComponent, reloadPage = location.reload.bind(location)) {
    this.#apiAdapter = apiAdapter;
    this.#clientDetailComponent = clientDetailComponent;
    this.#notificationComponent = notificationComponent;
    this.#reloadPage = reloadPage;
  }

  connect() {
    document.addEventListener('template-add', this.#onTemplateAdd);
    document.addEventListener('template-delete', this.#onTemplateDelete);
    document.addEventListener('site-republish', this.#onSiteRepublish);
    document.addEventListener('template-edit-request', this.#onTemplateEditRequest);
    document.addEventListener('template-update', this.#onTemplateUpdate);
    document.addEventListener('client-update', this.#onClientUpdate);
    document.addEventListener('billing-save', this.#onBillingSave);
    document.addEventListener('billing-delete', this.#onBillingDelete);
    document.addEventListener('billing-changed', this.#onBillingChanged);
    document.addEventListener('billing-history-request', this.#onHistoryRequest);
    document.addEventListener('billing-invoice-undo', this.#onInvoiceUndo);
    document.addEventListener('billing-invoice-cost', this.#onInvoiceCost);
    document.addEventListener('one-off-save', this.#onOneOffSave);
    document.addEventListener('one-off-delete', this.#onOneOffDelete);
  }

  disconnect() {
    document.removeEventListener('template-add', this.#onTemplateAdd);
    document.removeEventListener('template-delete', this.#onTemplateDelete);
    document.removeEventListener('site-republish', this.#onSiteRepublish);
    document.removeEventListener('template-edit-request', this.#onTemplateEditRequest);
    document.removeEventListener('template-update', this.#onTemplateUpdate);
    document.removeEventListener('client-update', this.#onClientUpdate);
    document.removeEventListener('billing-save', this.#onBillingSave);
    document.removeEventListener('billing-delete', this.#onBillingDelete);
    document.removeEventListener('billing-changed', this.#onBillingChanged);
    document.removeEventListener('billing-history-request', this.#onHistoryRequest);
    document.removeEventListener('billing-invoice-undo', this.#onInvoiceUndo);
    document.removeEventListener('billing-invoice-cost', this.#onInvoiceCost);
    document.removeEventListener('one-off-save', this.#onOneOffSave);
    document.removeEventListener('one-off-delete', this.#onOneOffDelete);
  }

  async loadClient(slug) {
    this.#clientDetailComponent.prepareClient(slug);
    // Taken before the client loads, so a billing answer still in flight for the client that was
    // on screen is dropped instead of showing on this one.
    const billingLoad = ++this.#billingLoads;
    this.#clientDetailComponent.loading = true;
    try {
      const apiClient = await this.#apiAdapter.getClient(slug);
      this.#clientDetailComponent.client = toClientViewModel(apiClient);
      this.#notificationComponent.clearError();
      await this.#loadBilling(slug, billingLoad);
    } catch (error) {
      this.#handleError(error, 'Could not load client');
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  }

  /**
   * Shows the client's billing items and one-off invoices; a failure is shown in the section it concerns, not as
   * a page failure.
   */
  async #loadBilling(slug, load) {
    await Promise.all([this.#loadItems(slug, load), this.#loadOneOffIncomes(slug, load)]);
  }

  async #loadItems(slug, load) {
    try {
      const items = await this.#apiAdapter.getClientBilling(slug);
      if (load === this.#billingLoads) {
        this.#clientDetailComponent.billingCard.items = items;
      }
    } catch (error) {
      if (load === this.#billingLoads) {
        this.#handleError(error, 'Could not load billing items', {
          show: (message) => {
            this.#clientDetailComponent.billingCard.showLoadError(message);
            return true;
          },
        });
      }
    }
  }

  async #loadOneOffIncomes(slug, load) {
    const section = this.#clientDetailComponent.oneOffIncomes;
    try {
      const incomes = await this.#apiAdapter.getClientOneOffIncomes(slug);
      if (load === this.#billingLoads) {
        section.incomes = incomes;
      }
    } catch (error) {
      if (load === this.#billingLoads) {
        this.#handleError(error, 'Could not load one-off invoices', {
          show: (message) => {
            section.showLoadError(message);
            return true;
          },
        });
      }
    }
  }

  /**
   * Reloads the billing card after any billing change (this card's own or an invoice recorded
   * elsewhere), but only while a client is on screen.
   */
  #onBillingChanged = () => {
    const slug = this.#clientDetailComponent.currentSlug;
    if (slug && this.#clientDetailComponent.style.display !== 'none') {
      this.#loadBilling(slug, ++this.#billingLoads);
    }
  };

  /**
   * Creates the item when `id` is null, else updates it. A rejected save keeps the form open with
   * the reason shown in it, so the input can be corrected.
   */
  #onBillingSave = async (event) => {
    const { slug, id, request } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      const saved = id === null
        ? await this.#apiAdapter.createBillingItem(slug, request)
        : await this.#apiAdapter.updateBillingItem(id, request);
      this.#clientDetailComponent.billingCard.closeForm();
      this.#notificationComponent.show(id === null ? 'Billing item added' : 'Billing item updated', 'success');
      document.dispatchEvent(new CustomEvent('billing-changed'));
    } catch (error) {
      this.#handleError(error, 'Could not save billing item', {
        show: (message) => this.#clientDetailComponent.billingCard.showFormError(message),
        close: () => this.#clientDetailComponent.billingCard.closeForm(),
      });
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  /** Loads the invoices of an item whose history panel is open; a failure shows in that panel. */
  #onHistoryRequest = async (event) => {
    const { id } = event.detail;
    const load = (this.#historyLoads.get(id) ?? 0) + 1;
    this.#historyLoads.set(id, load);
    const card = this.#clientDetailComponent.billingCard;
    try {
      const invoices = await this.#apiAdapter.getBillingInvoices(id);
      if (load === this.#historyLoads.get(id)) {
        card.showHistory(id, invoices);
      }
    } catch (error) {
      if (load === this.#historyLoads.get(id)) {
        this.#handleError(error, 'Could not load invoices', {
          show: (message) => {
            card.showHistoryError(id, message);
            return true;
          },
        });
      }
    }
  };

  /**
   * Undoes the latest invoice of an item; the card, history, overview, badge and income refresh from
   * `billing-changed`. When the undo is refused because the invoice is gone or no longer the latest
   * (404 or 409), the row the user clicked is stale, so the item's history and the card's items are
   * requested again.
   */
  #onInvoiceUndo = async (event) => {
    const { itemId, invoiceId } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      const item = await this.#apiAdapter.undoInvoice(invoiceId);
      this.#notificationComponent.show('Invoice undone', 'success');
      document.dispatchEvent(new CustomEvent('billing-changed'));
    } catch (error) {
      this.#handleError(error, 'Could not undo the invoice');
      if (error.status === 404 || error.status === 409) {
        this.#refreshStaleHistory(itemId);
      }
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  /**
   * Corrects the cost of a recorded invoice. A 404 means the invoice or its item is gone, so the item's history
   * and the card's items are requested again.
   */
  #onInvoiceCost = async (event) => {
    const { itemId, invoiceId, request } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      await this.#apiAdapter.correctInvoiceCost(invoiceId, request);
      this.#notificationComponent.show('Invoice cost updated', 'success');
      document.dispatchEvent(new CustomEvent('billing-changed'));
    } catch (error) {
      this.#handleError(error, 'Could not update the invoice cost');
      if (error.status === 404) {
        this.#refreshStaleHistory(itemId);
      }
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  /**
   * Creates the one-off invoice when `id` is null, else updates it; a rejected save keeps the form open. A 404
   * means the invoice or its client is gone, so the failure is a toast (the form may close) and the list reloads.
   */
  #onOneOffSave = async (event) => {
    const { slug, id, request } = event.detail;
    const section = this.#clientDetailComponent.oneOffIncomes;
    this.#clientDetailComponent.loading = true;
    try {
      if (id === null) {
        await this.#apiAdapter.createOneOffIncome(slug, request);
      } else {
        await this.#apiAdapter.updateOneOffIncome(id, request);
      }
      section.closeForm();
      this.#notificationComponent.show(id === null ? 'One-off invoice added' : 'One-off invoice updated', 'success');
      document.dispatchEvent(new CustomEvent('billing-changed'));
    } catch (error) {
      this.#handleError(error, 'Could not save one-off invoice', {
        show: (message) => error.status !== 404 && section.showFormError(message),
        close: () => section.closeForm(),
      });
      if (error.status === 404) {
        this.#reloadBilling();
      }
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  /** A 404 means the invoice is already gone, so the list reloads. */
  #onOneOffDelete = async (event) => {
    const { id, label } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      await this.#apiAdapter.deleteOneOffIncome(id);
      this.#notificationComponent.show(`One-off invoice "${label}" deleted`, 'success');
      document.dispatchEvent(new CustomEvent('billing-changed'));
    } catch (error) {
      this.#handleError(error, 'Could not delete one-off invoice');
      if (error.status === 404) {
        this.#reloadBilling();
      }
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  #refreshStaleHistory(itemId) {
    this.#onHistoryRequest({ detail: { id: itemId } });
    this.#reloadBilling();
  }

  /** Requests the billing items and one-off invoices of the client on screen again. */
  #reloadBilling() {
    const slug = this.#clientDetailComponent.currentSlug;
    if (slug) {
      this.#loadBilling(slug, ++this.#billingLoads);
    }
  }

  #onBillingDelete = async (event) => {
    const { id, label } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      await this.#apiAdapter.deleteBillingItem(id);
      this.#notificationComponent.show(`Billing item "${label}" deleted`, 'success');
      document.dispatchEvent(new CustomEvent('billing-changed'));
    } catch (error) {
      this.#handleError(error, 'Could not delete billing item');
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  /**
   * Closes the settings dialog only once the settings are saved, so a rejected save keeps what was
   * typed; the dialog itself says why the save was rejected.
   */
  #onClientUpdate = async (event) => {
    const { slug } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      const request = toCreateClientRequest(event.detail);
      const updated = await this.#apiAdapter.updateClient(slug, request);
      this.#clientDetailComponent.client = toClientViewModel(updated);
      this.#clientDetailComponent.closeClientSettings();
      this.#notificationComponent.show('Client settings updated', 'success');
    } catch (error) {
      this.#handleError(error, 'Could not update client settings', {
        show: (message) => this.#clientDetailComponent.showClientSettingsError(message),
        close: () => this.#clientDetailComponent.closeClientSettings(),
      });
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  #onTemplateAdd = async (event) => {
    const { slug } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      const request = toAddTemplateRequest(event.detail);
      const updated = await this.#apiAdapter.addTemplate(slug, request);
      this.#clientDetailComponent.client = toClientViewModel(updated);
      this.#clientDetailComponent.resetTemplateForm();
      this.#clientDetailComponent.revealTemplate(request.name);
      this.#notificationComponent.show('Template added', 'success');
    } catch (error) {
      this.#handleTemplateSaveError(error, 'Could not add template');
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  #onTemplateDelete = async (event) => {
    const { slug, templateName } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      const updated = await this.#apiAdapter.removeTemplate(slug, templateName);
      this.#clientDetailComponent.client = toClientViewModel(updated);
      this.#notificationComponent.show(`Template "${templateName}" deleted`, 'success');
    } catch (error) {
      this.#handleError(error, 'Could not delete template');
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  #onSiteRepublish = async (event) => {
    const { slug } = event.detail;
    this.#clientDetailComponent.loadingText = 'Republishing all pages...';
    this.#clientDetailComponent.loading = true;
    try {
      const result = await this.#apiAdapter.republishAll(slug);
      this.#notificationComponent.show(republishSummary(result), 'success');
    } catch (error) {
      this.#handleError(error, 'Could not republish pages');
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  #onTemplateEditRequest = async (event) => {
    const { slug, templateName } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      const templateData = await this.#apiAdapter.getTemplate(slug, templateName);
      this.#clientDetailComponent.enterEditMode(templateData);
      this.#notificationComponent.clearError();
    } catch (error) {
      this.#handleError(error, 'Could not open template');
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  #onTemplateUpdate = async (event) => {
    const { slug, templateName } = event.detail;
    this.#clientDetailComponent.loading = true;
    try {
      const request = toUpdateTemplateRequest(event.detail);
      const updatedTemplate = await this.#apiAdapter.updateTemplate(slug, templateName, request);
      this.#clientDetailComponent.enterEditMode(updatedTemplate);
      this.#notificationComponent.show('Template updated', 'success');
    } catch (error) {
      this.#handleTemplateSaveError(error, 'Could not save template');
    } finally {
      this.#clientDetailComponent.loading = false;
    }
  };

  /**
   * Shows a rejected template save next to the form instead of in a toast: template syntax and
   * field schema errors can be long, and the form is where they get fixed.
   *
   * @param {{status?: number, message: string}} error
   * @param {string} action what failed, shown before the server's message
   */
  #handleTemplateSaveError(error, action) {
    this.#handleError(error, action, {
      show: (message) => {
        this.#clientDetailComponent.showTemplateError(message);
        return true;
      },
    });
  }

  /**
   * Shows why a request failed, once: next to what the user was doing when `inline` can show it
   * there, else in a toast. A dialog closed while the request ran can no longer show it, so the
   * toast does. An expired session always goes to the toast, since the app reloads to the login
   * screen right after; an open dialog closes first, as its backdrop would cover the toast.
   *
   * @param {{status?: number, message: string}} error
   * @param {string} action what failed, shown before the server's message
   * @param {{show: (message: string) => boolean, close?: () => void}} [inline] `show` returns
   *     whether it could show the message; `close` closes the dialog that shows it
   */
  #handleError(error, action, inline) {
    if (error.status === 401) {
      inline?.close?.();
      this.#notificationComponent.show('Session expired. Please login again.', 'error');
      setTimeout(() => this.#reloadPage(), 2000);
    } else if (!inline?.show(`${action}: ${error.message}`)) {
      this.#notificationComponent.show(`${action}: ${error.message}`, 'error');
    }
  }
}
